import React, { useState } from 'react';
import { APP_CONFIG } from '../config';
import './TelegramNotify.css';

export function TelegramNotify({ userId, userData, userInvestments, onClose, addNotification }) {
  const [telegramId, setTelegramId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLinked, setIsLinked] = useState(false);

  const generateReportData = () => {
    return {
      userId: userId,
      totalInvested: parseFloat(userData?.totalInvestment || 0),
      totalEarned: parseFloat(userData?.totalEarned || 0),
      pendingRewards: parseFloat(userData?.pendingRewards || 0),
      activeInvestments: userInvestments?.filter(inv => inv.active).length || 0,
      isActive: userData?.isActive ?? true
    };
  };

  const sendTelegramReport = async () => {
    if (!telegramId.trim()) {
      addNotification('error', 'Please enter your Telegram Chat ID');
      return;
    }

    setIsSending(true);
    
    try {
      const reportData = generateReportData();
      
      // Send via PHP backend
      const response = await fetch(APP_CONFIG.telegramApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: telegramId,
          report: reportData
        })
      });

      const data = await response.json();
      
      if (data.success) {
        addNotification('success', 'Report sent to Telegram! You will now receive automatic notifications.');
        setIsLinked(true);
        localStorage.setItem('alphameta_telegram_id', telegramId);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      addNotification('error', `Failed: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Load saved Telegram ID
  React.useEffect(() => {
    const savedId = localStorage.getItem('alphameta_telegram_id');
    if (savedId) {
      setTelegramId(savedId);
      setIsLinked(true);
    }
  }, []);

  return (
    <div className="telegram-modal-overlay" onClick={onClose}>
      <div className="telegram-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        
        <div className="telegram-header">
          <div className="telegram-icon">📱</div>
          <h2>Telegram Notifications</h2>
          <p>Get instant alerts for all your activities</p>
        </div>

        <div className="telegram-steps">
          <div className="step-item">
            <span className="step-number">1</span>
            <div className="step-content">
              <span className="step-title">Start our Bot</span>
              <span className="step-desc">
                Click to copy and search on Telegram:
                <div className="bot-username-box">
                  <code className="bot-username">{APP_CONFIG.telegramBotUsername}</code>
                  <button 
                    className="copy-username-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(APP_CONFIG.telegramBotUsername);
                      addNotification('success', 'Bot username copied!');
                    }}
                    title="Copy username"
                  >
                    📋
                  </button>
                </div>
                
              </span>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <div className="step-content">
              <span className="step-title">Get Your Chat ID</span>
              <span className="step-desc">Click <code>START</code> or send <code>/start</code> to get your Chat ID</span>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <div className="step-content">
              <span className="step-title">Enter Chat ID Below</span>
              <span className="step-desc">Paste your Chat ID to enable notifications</span>
            </div>
          </div>
        </div>

        <div className="input-group">
          <label>Telegram Chat ID</label>
          <input
            type="text"
            placeholder="e.g., 123456789"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
          />
        </div>

        {isLinked && (
          <div className="linked-status">
            <span className="status-icon">✓</span>
            <span>Telegram linked! Auto-notifications enabled.</span>
          </div>
        )}

        <div className="telegram-actions">
          <button 
            className="send-report-btn"
            onClick={sendTelegramReport}
            disabled={isSending || !telegramId.trim()}
          >
            {isSending ? (
              <><span className="btn-spinner" />Sending...</>
            ) : isLinked ? (
              <>📤 Send Report Now</>
            ) : (
              <>🔗 Link & Enable Notifications</>
            )}
          </button>
        </div>

        <div className="telegram-info">
          <p>🔔 *Auto-notifications* for:</p>
          <ul>
            <li>💎 New Investments</li>
            <li>🚀 Trade Started</li>
            <li>💰 Profit Claimed</li>
            <li>🏧 Withdrawals</li>
            <li>👥 Referral Bonuses</li>
          </ul>
          <p style={{marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-muted)'}}>
            Plus manual portfolio reports anytime!
          </p>
        </div>
      </div>
    </div>
  );
}

export default TelegramNotify;
